import { API_BASE_URL } from '../config/constants';
import { getAuthHeaders } from './api';

export interface NotificationResponse {
  id: string;
  notification_type: string;
  priority: string;
  message_type: string;
  title: string;
  message: string;
  summary?: string;
  icon?: string;
  icon_color?: string;
  background_color?: string;
  action_url?: string;
  action_text?: string;
  action_type?: string;
  is_read: boolean;
  is_deleted: boolean;
  created_at?: string;
  read_at?: string;
  clicked_at?: string;
  expires_at?: string;
  age_in_days: number;
  order_id?: string;
  related_entity_id?: string;
  related_entity_type?: string;
}

export interface NotificationPreview {
  id: string;
  title: string;
  summary?: string;
  icon?: string;
  icon_color?: string;
  priority: string;
  is_read: boolean;
  created_at?: string;
  action_url?: string;
}

export interface NotificationSummary {
  unread_count: number;
  has_urgent: boolean;
  has_high_priority: boolean;
  recent_notifications: NotificationPreview[];
}

export interface NotificationListResponse {
  success: boolean;
  notifications: NotificationResponse[];
  total_count: number;
  unread_count: number;
  page: number;
  size: number;
  total_pages: number;
}

export interface NotificationFilters {
  page?: number;
  size?: number;
  include_read?: boolean;
  priority_filter?: string;
  search?: string;
  sort_by?: string;
  sort_order?: string;
}

class NotificationService {
  private baseUrl = `${API_BASE_URL}/notifications`;

  async getNotificationSummary(): Promise<NotificationSummary> {
    const response = await fetch(`${this.baseUrl}/summary`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch notification summary');
    }

    return response.json();
  }

  async getNotifications(filters: NotificationFilters = {}): Promise<NotificationListResponse> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.size) params.append('size', filters.size.toString());
    if (filters.include_read !== undefined) params.append('include_read', filters.include_read.toString());
    if (filters.priority_filter) params.append('priority_filter', filters.priority_filter);
    if (filters.search) params.append('search', filters.search);
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_order) params.append('sort_order', filters.sort_order);

    const response = await fetch(`${this.baseUrl}/?${params}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch notifications');
    }

    return response.json();
  }

  async markAsRead(notificationId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${notificationId}/read`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to mark notification as read');
    }
  }

  async markAsClicked(notificationId: string): Promise<{ action_url?: string; action_type?: string }> {
    const response = await fetch(`${this.baseUrl}/${notificationId}/click`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to mark notification as clicked');
    }

    return response.json();
  }

  async deleteNotification(notificationId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${notificationId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete notification');
    }
  }

  async markAllAsRead(): Promise<void> {
    const response = await fetch(`${this.baseUrl}/mark-all-read`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to mark all notifications as read');
    }
  }

  async deleteAllRead(): Promise<void> {
    const response = await fetch(`${this.baseUrl}/delete-all-read`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete all read notifications');
    }
  }

  async markMultipleAsRead(notificationIds: string[]): Promise<void> {
    const response = await fetch(`${this.baseUrl}/mark-multiple-read`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ notification_ids: notificationIds }),
    });

    if (!response.ok) {
      throw new Error('Failed to mark multiple notifications as read');
    }
  }

  async markMultipleAsDeleted(notificationIds: string[]): Promise<void> {
    const response = await fetch(`${this.baseUrl}/mark-multiple-delete`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ notification_ids: notificationIds }),
    });

    if (!response.ok) {
      throw new Error('Failed to delete multiple notifications');
    }
  }

  // Utility methods
  getTimeAgo(dateString?: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Baru saja';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} menit lalu`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} jam lalu`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} hari lalu`;
    
    return date.toLocaleDateString('id-ID');
  }

  getPriorityColor(priority: string): string {
    switch (priority.toLowerCase()) {
      case 'urgent': return '#f44336';
      case 'high': return '#ff9800';
      case 'normal': return '#2196f3';
      case 'low': return '#9e9e9e';
      default: return '#2196f3';
    }
  }

  getMessageTypeIcon(messageType: string): string {
    switch (messageType.toLowerCase()) {
      case 'transactional': return 'receipt_long';
      case 'promotional': return 'campaign';
      case 'informational': return 'info';
      default: return 'notifications';
    }
  }

  getMessageTypeBadgeColor(messageType: string): string {
    switch (messageType.toLowerCase()) {
      case 'transactional': return '#e8f5e8';
      case 'promotional': return '#fff3e0';
      case 'informational': return '#e3f2fd';
      default: return '#f5f5f5';
    }
  }
}

export const notificationService = new NotificationService();